import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowLeft, ArrowRight, Phone } from "lucide-react";
import { Button } from "../components/ui/button";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { CtaBanner } from "../components/CtaBanner";
import { servicePages, getServiceBySlug } from "../data/servicePages";
import { useSeo } from "../hooks/useSeo";

const SITE = process.env.REACT_APP_SITE_URL || window.location.origin;

export default function ServicePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const page = getServiceBySlug(slug);
  const canonical = page ? `${SITE}/services/${page.slug}` : SITE;

  const jsonLd = page
    ? {
        "@context": "https://schema.org",
        "@type": "Service",
        name: page.h1,
        serviceType: page.nav,
        description: page.description,
        provider: {
          "@type": "ProfessionalService",
          name: "Snesar Aerial Intelligence Pvt Ltd",
          telephone: "+91-7715972997",
          address: {
            "@type": "PostalAddress",
            addressLocality: "Navi Mumbai",
            addressRegion: "Maharashtra",
            addressCountry: "IN",
          },
        },
        areaServed: "India",
        url: canonical,
      }
    : null;

  useSeo({
    title: page?.title,
    description: page?.description,
    keywords: page?.keywords,
    canonical,
    image: page?.hero,
    jsonLd,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!page) navigate("/", { replace: true });
  }, [slug, page, navigate]);

  if (!page) return null;

  const related = servicePages.filter((p) => p.slug !== slug).slice(0, 4);

  return (
    <div className="min-h-screen bg-white antialiased">
      <Navbar />

      <main className="pt-20">
        {/* Hero */}
        <section className="relative">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Link
                to="/#services"
                data-testid="service-back-link"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#8b8f99] hover:text-[#10193f]"
              >
                <ArrowLeft className="h-4 w-4" /> All Services
              </Link>
              <h1 className="mt-5 font-heading text-4xl sm:text-5xl font-black text-[#10193f] leading-[1.05]">
                {page.h1}
              </h1>
              <p className="mt-5 text-base sm:text-lg text-[#52525b] leading-relaxed">
                {page.lead}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button
                  data-testid="service-cta-quote"
                  onClick={() => navigate("/#contact")}
                  className="rounded-sm bg-[#10193f] hover:bg-[#1a2350] text-white font-semibold h-12 px-7"
                >
                  Request a Quote <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <a href="tel:+917715972997">
                  <Button
                    variant="outline"
                    data-testid="service-cta-call"
                    className="rounded-sm border-[#10193f]/25 text-[#10193f] hover:bg-[#10193f]/5 font-semibold h-12 px-7 w-full"
                  >
                    <Phone className="mr-2 h-4 w-4" /> 7715972997
                  </Button>
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="relative h-72 sm:h-96 rounded-sm overflow-hidden border border-gray-200 bg-[#f2f4f8]"
            >
              <img
                src={page.hero}
                alt={page.h1}
                className={`w-full h-full ${page.heroFit}`}
              />
            </motion.div>
          </div>
        </section>

        {/* Content sections */}
        <section className="bg-[#f8f9fa] border-y border-gray-200 py-16">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-12">
            {page.sections.map((s) => (
              <div key={s.heading} data-testid="service-section">
                <h2 className="font-heading text-2xl font-black text-[#10193f]">
                  {s.heading}
                </h2>
                <ul className="mt-6 space-y-3">
                  {s.points.map((p) => (
                    <li
                      key={p}
                      className="flex items-start gap-3 text-[#52525b]"
                    >
                      <Check className="mt-1 h-5 w-5 shrink-0 text-[#8b8f99]" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Flagship highlight */}
        {page.highlight && (
          <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
            <div
              data-testid="service-highlight"
              className="rounded-sm bg-[#10193f] text-white p-8 sm:p-10"
            >
              <span className="text-xs font-bold uppercase tracking-widest text-[#8b8f99]">
                Flagship Project
              </span>
              <p className="mt-3 font-heading text-xl sm:text-2xl font-bold">
                {page.highlight}
              </p>
            </div>
          </section>
        )}

        {/* Related services */}
        <section className="bg-[#f8f9fa] border-t border-gray-200 py-16">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <h2 className="font-heading text-2xl font-black text-[#10193f]">
              Related Drone Services
            </h2>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/services/${r.slug}`}
                  data-testid={`related-${r.slug}`}
                  className="group border border-gray-200 rounded-sm p-6 bg-white hover:border-[#10193f]/40 hover:-translate-y-1 transition-all duration-300"
                >
                  <h3 className="font-heading text-lg font-bold text-[#10193f]">
                    {r.nav}
                  </h3>
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#8b8f99] group-hover:text-[#10193f]">
                    Learn more <ArrowRight className="h-4 w-4" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <CtaBanner />
      <Footer />
    </div>
  );
}
